import { isAndroid, isIOS } from "@/utils/platform";
import { getTodayDate } from "../dailyTracking";
import appStore from "@/utils/store";
import { isToday } from "date-fns";
import { calcDuration, calculateDailyCaloricNeeds, calculateTrainingCalories, getUserWeight } from "@/utils/workouts";
import { logEvent } from "@/utils/logs";

export function onDeviceReady() {
    return new Promise<void>((resolve) => {
        if ((window as any).cordova) {
            document.addEventListener('deviceready', () => {
                console.log("📱 Device ready");
                resolve();
            }, false);
        } else {
            console.warn("⚠️ Cordova not detected, skipping deviceready wait");
            resolve();
        }
    });
}

export async function detectHealthProvider(): Promise<string | null> {
    console.log("🔍 Detecting health provider...");

    return new Promise((resolve) => {
        if (!window.cordova || !cordova.plugins || !cordova.plugins.health) {
            console.warn("❌ Cordova health plugin not available");
            appStore.set("healthProvider", null);
            return resolve(null);
        }

        cordova.plugins.health.isAvailable(
            (available: boolean) => {
                if (available) {
                    let provider: string | null = null;
                    if (isIOS()) {
                        provider = 'apple.health';
                    } else if (isAndroid()) {
                        provider = 'android.health';
                    }
                    appStore.set("healthProvider", provider);
                    console.log("✅ Health services available:", provider);
                    resolve(provider);
                } else {
                    console.warn("❌ Health services not available on this device");
                    appStore.set("healthProvider", null);
                    resolve(null);
                }
            },
            (error: any) => {
                console.warn("❌ Error checking health availability:", error);
                appStore.set("healthProvider", null);
                resolve(null);
            }
        );
    });
}

export async function requestHealthAuthorization(): Promise<boolean> {
    console.log("🔍 Requesting health authorization...");
    if (appStore.get("settingsCaloo")?.healthIntegration === false) {
        console.log("⚠️ Health integration is disabled in settings");
        return false;
    }
    const datatypes = {
        read: ['steps', 'calories', 'activity', 'sleep', 'weight'],
        write: ['calories']
    };

    if (!window.cordova || !cordova.plugins || !cordova.plugins.health) {
        console.warn("⚠️ Cordova Health plugin not available on requestHealthAuthorization.");
        appStore.set("healthProvider", null);
        return false;
    }

    return new Promise((resolve) => {
        try {
            cordova.plugins.health.requestAuthorization(
                datatypes,
                () => {
                    let provider: string | null = null;
                    if (isIOS()) {
                        provider = 'apple.health';
                    } else if (isAndroid()) {
                        provider = 'android.health';
                    }
                    appStore.set("healthProvider", provider);
                    console.log("✅ Autorización concedida");
                    resolve(true);
                },
                (err: any) => {
                    appStore.set("healthProvider", null);
                    console.error("❌ Autorización fallida", err);
                    resolve(false);
                }
            );
        } catch (error) {
            console.error("❌ Error requesting health authorization:", error);
            alert("Error requesting health authorization. Please check your device settings.");
            appStore.set("healthProvider", null);
            resolve(false);
        }
    });
}

export async function loadDailyHealthData(date = getTodayDate()) {
    try {
        if (appStore.get("settingsCaloo")?.healthIntegration === false) {
            console.log("⚠️ Health integration is disabled in settings");
            return false;
        }

        //console.log(`🔍 Requesting health data for ${date}...`);
        await requestHealthAuthorization();
        let steps: { steps: number | null, isToday: boolean } = { steps: null, isToday: false };
        let workouts = { totalCalories: null, activities: [], isToday: false };
        let sleep = { totalHours: null, periods: [], isToday: false };
        let weight = { weight: null, timestamp: null, isToday: false };

        //console.log("⏳ Getting steps...");
        try {
            steps = await getTodaySteps(date);
            //console.log("✅ Steps loaded", JSON.stringify(steps));
        } catch (e) {
            console.error("❌ Error loading steps:", e);
        }

        //console.log("⏳ Getting workouts...");
        try {
            workouts = await getTodayWorkouts(date);
            //console.log("✅ Workouts loaded", JSON.stringify(workouts));
        } catch (e) {
            console.error("❌ Error loading workouts:", e);
        }

        //console.log("⏳ Getting sleep...");
        try {
            sleep = await getTodaySleep(date);
            //console.log("✅ Sleep loaded", JSON.stringify(sleep));
        } catch (e) {
            console.error("❌ Error loading sleep:", e);
        }

        //console.log("⏳ Getting weight...");
        try {
            weight = await getTodayWeight(date);
            //console.log("✅ Weight loaded", JSON.stringify(weight));
        } catch (e) {
            console.error("❌ Error loading weight:", e);
        }

        appStore.set("healthData", {
            date,
            steps,
            workouts,
            sleep,
            weight,
        });

        console.log(`✅ Health data fully loaded for ${date}`);

        return {
            steps,
            workouts,
            sleep,
            weight,
        };

    } catch (e) {
        logEvent(`❌ Error initializing health data: ${e}`);
        return {
            steps: { steps: null, isToday: false },
            workouts: { totalCalories: null, activities: [], isToday: false },
            sleep: { totalHours: null, periods: [], isToday: false },
            weight: { weight: null, timestamp: null, isToday: false },
        };
    }
}

export function getTodayWorkouts(date = getTodayDate()): Promise<{
    totalCalories: number | null,
    activities: any[],
    isToday: boolean | false
}> {
    const today = new Date(date);
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    return new Promise((resolve, reject) => {
        // Verifica si Cordova y el plugin están disponibles
        if (
            typeof window === "undefined" ||
            typeof cordova === "undefined" ||
            !cordova.plugins ||
            !cordova.plugins.health ||
            typeof cordova.plugins.health.query !== "function" ||
            typeof cordova.plugins.health.queryAggregated !== "function"
        ) {
            console.warn("⚠️ Cordova Health plugin no disponible. Retornando datos vacíos.");
            return resolve({
                totalCalories: null,
                activities: [],
                isToday: false
            });
        }

        // Primero consulta todas las calorías activas del día
        cordova.plugins.health.queryAggregated(
            {
                startDate: startOfDay,
                endDate: endOfDay,
                dataType: "calories.active",
            },
            (aggregateResult: any) => {
                console.log(
                    "🔥 Total de calorías activas del día (aggregated):",
                    aggregateResult
                );
                const totalDayCalories =
                    typeof aggregateResult.value === "number"
                        ? parseFloat(aggregateResult.value.toFixed(2))
                        : 0;
                console.log("🔥 Total calorías activas del día:", totalDayCalories);

                // Ahora consulta actividades detalladas
                cordova.plugins.health.query(
                    {
                        startDate: startOfDay,
                        endDate: endOfDay,
                        dataType: 'activity'
                    },
                    async (activities: any[]) => {
                        const activityDetails: any[] = [];

                        for (const activity of activities) {
                            const calories = await new Promise<number>((res, rej) => {
                                cordova.plugins.health.query(
                                    {
                                        startDate: new Date(activity.startDate),
                                        endDate: new Date(activity.endDate),
                                        dataType: 'calories.active'
                                    },
                                    (calResult: any[]) => {
                                        const total = calResult.reduce((acc, curr) => acc + (Number(curr.value) || 0), 0);
                                        // console.log(`🔥 Calorías para actividad ${activity.value}:`, total);
                                        if (!total || total === 0) {
                                            logEvent(`⚠️ No se encontraron calorías para actividad: ${activity.value}`);
                                            const user = appStore.get("userPhenotype");
                                            const kcal = calculateTrainingCalories({
                                                goal: user?.goal || "1",
                                                activityLevel: user?.activityType || "1",
                                                weight: getUserWeight(),
                                                trainingType: activity.value,
                                                duration: calcDuration(activity.startDate, activity.endDate),
                                                dailyKcal: calculateDailyCaloricNeeds(user),
                                            });
                                            res(kcal)
                                        } else {
                                            res(total);
                                        }
                                    },
                                    (err) => {
                                        logEvent(`⚠️ ❌ Error al consultar calorías para actividad: ${activity} - ${err}`);
                                        res(0);
                                    }
                                );
                            });

                            activityDetails.push({
                                type: activity.value,
                                label: activity.value.charAt(0).toUpperCase() + activity.value.slice(1),
                                kcal: calories ? parseFloat(calories.toFixed(2)) : null,
                                startTime: new Date(activity.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                                duration: Math.round(activity.duration / 60),
                                startDate: activity.startDate,
                                endDate: activity.endDate
                            });
                        }

                        // Como totalCalories ya es el total del día, no sumamos actividades aparte
                        resolve({
                            totalCalories: parseFloat(totalDayCalories.toFixed(2)),
                            activities: activityDetails,
                            isToday: totalDayCalories > 0
                        });
                    },
                    (err: any) => {
                        logEvent(`❌ Error al consultar actividades: ${err}`);
                        // Aunque fallen actividades, devolvemos las calorías del día
                        resolve({
                            totalCalories: parseFloat(totalDayCalories.toFixed(2)),
                            activities: [],
                            isToday: totalDayCalories > 0
                        });
                    }
                );
            },
            (err) => {
                logEvent(`❌ Error al consultar calorías totales del día: ${err}`);
                reject(err);
            }
        );
    });
}
export function getTodaySleep(date = getTodayDate()): Promise<{ totalHours: number | null, periods: any[], isToday: boolean | false }> {
    const today = new Date(date);
    //console.log("📅 Fecha de hoy:", today);

    return new Promise((resolve, reject) => {
        if (!window.cordova || !cordova.plugins || !cordova.plugins.health) {
            console.warn("⚠️ Cordova Health plugin not available on getTodaySleep.");
            resolve({
                totalHours: null,
                periods: [],
                isToday: false
            });
            return;
        }
        const startOfNight = new Date(today);
        startOfNight.setDate(today.getDate() - 1);
        startOfNight.setHours(18, 0, 0, 0);  // 18:00 del día anterior

        const endOfMorning = new Date(today);
        endOfMorning.setHours(12, 0, 0, 0);  // 12:00 del día actual

        //console.log(`😴 Consultando sueño desde ${startOfNight.toISOString()} hasta ${endOfMorning.toISOString()}`);

        cordova.plugins.health.query(
            {
                startDate: startOfNight,
                endDate: endOfMorning,
                dataType: 'sleep'
            },
            (sleepData: any[]) => {
                // console.log("✅ Datos de sueño encontrados:", sleepData);

                if (!sleepData || sleepData.length === 0) {
                    resolve({
                        totalHours: null,
                        periods: [],
                        isToday: false
                    });
                    return;
                }

                resolve({
                    ...mergeSleepPeriods(sleepData), isToday: true
                });
            },
            (err: any) => {
                logEvent(`❌ Error al consultar sueño: ${err}`);
                reject(err);
            }
        );
    });
}
export function getTodayWeight(date = getTodayDate()): Promise<{ weight: number | null, timestamp: string | null, isToday: boolean | false }> {

    const today = new Date(date);
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    //console.log("⚖️ Consultando peso para:", date);

    return new Promise((resolve, reject) => {
        if (!window.cordova || !cordova.plugins || !cordova.plugins.health) {
            console.warn("⚠️ Cordova Health plugin not available on getTodayWeight.");
            resolve({ weight: null, timestamp: null, isToday: false });
            return;
        }
        cordova.plugins.health.query(
            {
                startDate: startOfDay,
                endDate: endOfDay,
                dataType: 'weight',
                limit: 1,
                descending: true // para obtener la última medición del día
            },
            (results: any[]) => {
                if (results.length > 0) {
                    const weightEntry = results[0];
                    const weight = parseFloat(weightEntry.value);
                    const timestamp = weightEntry.startDate || weightEntry.date;
                    console.log("✅ Peso encontrado:", weight, "kg");
                    resolve({ weight, timestamp, isToday: true });
                } else {
                    console.warn("⚠️ No se encontró peso registrado para hoy");
                    resolve({ weight: null, timestamp: null, isToday: false });
                }
            },
            (err: any) => {
                logEvent(`❌ Error al consultar el peso: ${err}`);
                reject(err);
            }
        );
    });
}
// Función para obtener los pasos de hoy
export async function getTodaySteps(date = getTodayDate()): Promise<{ steps: number | null, isToday: boolean }> {
    //console.log("👣 Consultando pasos para:", date);
    return new Promise((resolve) => {
        if (!window.cordova || !cordova.plugins || !cordova.plugins.health) {
            console.warn("⚠️ Cordova Health plugin not available on getTodaySteps.");
            resolve({ steps: null, isToday: false });
            return;
        }
        const today = new Date(date); // Cambia la fecha a la que quieras
        const start = new Date(today.setHours(0, 0, 0, 0)).getTime();
        const end = new Date().getTime();
        try {
            cordova.plugins.health.queryAggregated({
                startDate: new Date(start),
                endDate: new Date(end),
                dataType: 'steps',
                bucket: 'day'
            }, (data: any) => {
                //console.log("✅ Health data received - steps:", JSON.stringify(data));
                const result = data && data.length > 0 ? data[0].value : 0;
                resolve({ steps: result, isToday: !!result && result > 0 });
            }, (err: any) => {
                logEvent(`❌ Error al consultar los pasos del día: ${err}`);
                resolve({ steps: null, isToday: false });
            });
        } catch (error) {
            logEvent(`❌ Error global al consultar los pasos del día: ${err}`);
            resolve({ steps: null, isToday: false });
        }
    });
}

export function getHealthConnectFromStore() {
    if (!window.cordova || !cordova.plugins.health) {
        console.warn("⚠️ Cordova o el plugin de salud no están disponibles");
        return;
    }

    cordova.plugins.health.getHealthConnectFromStore(
        (available) => {
            if (available) {
                console.log("✅ Health Connect está disponible o ya instalado");
            } else {
                console.warn("❌ Health Connect no está disponible o no se pudo abrir el Play Store");
            }
        },
        (err) => {
            console.error("❌ Error al intentar abrir Health Connect en Play Store:", err);
        }
    );
}

export function launchPrivacyPolicy() {
    if (!window.cordova || !cordova.plugins.health) {
        console.warn("⚠️ Cordova o el plugin de salud no están disponibles");
        return;
    }

    cordova.plugins.health.launchPrivacyPolicy(
        () => {
            console.log("✅ Pantalla de política de privacidad abierta correctamente");
        },
        (err) => {
            console.error("❌ Error al abrir la política de privacidad de Health Connect:", err);
        }
    );
}

export function openHealthSettings() {
    if (!window.cordova || !cordova.plugins.health) {
        console.warn("⚠️ Cordova o el plugin de salud no están disponibles");
        return;
    }

    cordova.plugins.health.openHealthSettings(
        () => {
            console.log("✅ Ajustes de salud abiertos correctamente");
        },
        (err) => {
            console.error("❌ Error al abrir los ajustes de salud:", err);
        }
    );
}


export function getWorkoutIcon(type: string): string {
    const map: Record<string, string> = {
        tennis: "🎾",
        running: "🏃",
        walking: "🚶",
        cycling: "🚴",
        swimming: "🏊",
        yoga: "🧘",
        strengthTraining: "🏋️",
        hiking: "🥾",
        dance: "💃",
        elliptical: "🚴‍♀️",
        rower: "🚣",
        soccer: "⚽",
        basketball: "🏀",
        // añade más tipos si quieres
    };

    return map[type.toLowerCase()] || "🏋️"; // por defecto un icono genérico
}

export function formatDuration(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return [
        hrs > 0 ? String(hrs).padStart(2, '0') : null,
        String(mins).padStart(2, '0'),
        String(secs).padStart(2, '0')
    ].filter(Boolean).join(':');
}

export function mergeSleepPeriods(periods: any) {
    try {
        if (!periods || periods.length === 0) return { totalHours: 0, periods: [] };

        const validPeriods = periods
            .map(p => {
                const start = new Date(p.startDate);
                const end = new Date(p.endDate);
                if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                    console.warn("⛔ Periodo inválido descartado:", p);
                    return null;
                }
                return { ...p, start, end };
            })
            .filter(p => p !== null)
            .sort((a, b) => a.start - b.start);

        if (validPeriods.length === 0) return { totalHours: 0, periods: [] };

        const merged = [];
        let current = validPeriods[0];

        for (let i = 1; i < validPeriods.length; i++) {
            const next = validPeriods[i];
            if (next.start <= current.end) {
                current.end = new Date(Math.max(current.end.getTime(), next.end.getTime()));
            } else {
                merged.push({ start: current.start, end: current.end });
                current = next;
            }
        }
        merged.push({ start: current.start, end: current.end });

        const totalMs = merged.reduce((sum, p) => sum + (p.end - p.start), 0);
        const totalHours = Number((totalMs / 1000 / 60 / 60).toFixed(2));

        const periodsOut = merged.map(p => ({
            startTime: p.start.toTimeString().slice(0, 5),
            endTime: p.end.toTimeString().slice(0, 5),
            duration: Math.round((p.end - p.start) / 1000 / 60)
        }));
        // console.log("🌙 Periodos de sueño:", periodsOut, totalHours, totalMs);
        return { totalHours, periods: periodsOut };

    } catch (error) {
        console.error("❌ Error merging sleep periods:", error);
        return { totalHours: 0, periods };
    }
}